import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import _filter from 'lodash/filter';
import _sortBy from 'lodash/sortBy';
import { isDesktop } from 'react-device-detect';
import config from '../../../app/config';
import { canEnterAdminPage, hasOneOfPermission, hasPermission, Permissions, user } from '../../../store/reducers/user';
import { AVAILABLE_MODULES, MODULE_NAMES } from '../constants';

export default function useActiveModules(groups) {
    const dispatch = useDispatch();

    const userObject = useSelector(user);

    // User's active Agyle modules
    const [activeModules, setActiveModules] = useState([]);
    const [isModulesLoading, setIsModulesLoading] = useState(true);

    const canEnterAdmin = dispatch(canEnterAdminPage());
    const canAccessBA = dispatch(hasPermission(Permissions.BA_CAN_ACCESS_BA));
    const canViewScheduler = dispatch(hasPermission(Permissions.AGYLE_CAN_ACCESS_SCHEDULER));
    const canViewBedBooking = dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_BED_BOOKING));
    const canAccessMCT = dispatch(
        hasOneOfPermission([
            Permissions.AGYLE_CAN_ACCESS_MCT_ADMIN,
            Permissions.AGYLE_CAN_MANAGE_WARD_MAP,
            Permissions.AGYLE_CAN_PUBLISH_WARD_MAP,
            Permissions.AGYLE_CAN_VIEW_ONLY_WARD_MAP,
        ]),
    );
    const canAccessTreatmentBoardAdmin = dispatch(hasPermission(Permissions.AGYLE_CAN_MANAGE_TREATMENTBOARDS));

    const getModulePredicate = (module, isActive) => {
        switch (module?.name) {
            case MODULE_NAMES.ADMIN:
                return (isActive || config.adminModuleEnabledExceptionUsers.includes(userObject.userName)) && isDesktop && canEnterAdmin;
            case MODULE_NAMES.BED_BOOKING:
                return isActive && canViewBedBooking;
            case MODULE_NAMES.BOOK_ADMISSION:
            case MODULE_NAMES.BOOKED_ADMISSION:
            case MODULE_NAMES.BOOKED_ADMISSIONS_DASHBOARD:
                return isActive && canAccessBA;
            case MODULE_NAMES.SCHEDULER:
                return isActive && canViewScheduler;
            case MODULE_NAMES.MCT:
                return isActive && canAccessMCT;
            case MODULE_NAMES.TREATMENT_BOARD_MANAGEMENT:
                return isActive && canAccessTreatmentBoardAdmin;
            case MODULE_NAMES.EXTENDED_DASHBOARD_MANAGEMENT:
                return isActive && canAccessTreatmentBoardAdmin;
            default:
                return !!isActive;
        }
    };

    const findGroupByModule = (moduleName) => {
        const entry = Object.entries(groups).find(([, group]) => group.modules.includes(moduleName));

        if (!entry) return null;

        const [name, group] = entry;
        return {
            name,
            colour: group.colour,
        };
    };

    useEffect(() => {
        const modulesClone = _cloneDeep(AVAILABLE_MODULES);
        const active = _filter(modulesClone, (mod) => getModulePredicate(mod, !!mod?.enabled));
        const sortedModulesByName = _sortBy(active, (item) => item.description.toLowerCase());
        if (groups) {
            sortedModulesByName.forEach((mod) => {
                const { name, colour } = findGroupByModule(mod.id) || {};

                mod.groupName = name ?? null;
                mod.groupColor = colour ?? null;
            });
        }
        setActiveModules(sortedModulesByName);
        setIsModulesLoading(false);
    }, [AVAILABLE_MODULES, dispatch, userObject, canAccessBA, canViewBedBooking, canEnterAdmin, canAccessMCT]);

    return [activeModules, isModulesLoading];
}
